import { useCallback } from 'react';
import { type AnySearch, useRouter } from '@frontend/weave-router';
import { useInterRouter, useNonReactiveInterRouter } from './inter-router';
import { SettingsRoutes } from './settings-routes';
import type { SettingsSearchParams } from './settings-search-param-types';
import type { PathSegments, RouteParams } from './types';
import { useConstructSettingsLabel } from './use-construct-settings-label';

type BaseNavigateArgs<T extends SettingsRoutes, C extends unknown = any> = {
  to: T;
  originLabel?: string;
  search?: SettingsSearchParams<T>;
  context?: C;
  replace?: boolean;
};

type MainNavigateArgs = {
  to: string;
  search?: AnySearch;
  replace?: boolean;
};

export type NavigateArgs<T extends SettingsRoutes, C extends unknown = any> = PathSegments<T> extends never
  ? BaseNavigateArgs<T, C>
  : BaseNavigateArgs<T, C> & { params: Partial<RouteParams<T>> };

export const useSettingsNavigate = <C extends unknown = any>() => {
  const {
    setSettingsRouterPath,
    goBackSettings,
    getBackSettings,
    setSettingsRouterPathHistory,
    closeSettings,
    isSettingsOpen,
    setIsSettingsOpen,
    settingsRouterPath,
    setOriginPageLabel,
    context,
    setMainRouterPath,
  } = useInterRouter([
    'setSettingsRouterPath',
    'goBackSettings',
    'getBackSettings',
    'setSettingsRouterPathHistory',
    'closeSettings',
    'isSettingsOpen',
    'setIsSettingsOpen',
    'settingsRouterPath',
    'setOriginPageLabel',
    'context',
    'setMainRouterPath',
  ]);
  //using the new router history because it will always be available
  const router = useRouter();
  const history = router.history;
  const constructLabel = useConstructSettingsLabel();
  const nonReactiveInterRouter = useNonReactiveInterRouter();
  /**
   * Navigates to a specific settings route.
   * Called programmatically to navigate within settings routes. The main navigate function returned and used from
   * useSettingsNavigate hook.
   * NOTE: this is only used to navigate within the settings context and will not work to navigate to a route
   * outside of the settings context.
   */
  const navigate = useCallback(
    function <T extends SettingsRoutes, C extends unknown = any>({
      to,
      originLabel,
      search,
      context,
      replace,
      ...rest
    }: NavigateArgs<T, C>) {
      let newPath: string = to;

      if ('params' in rest && rest.params) {
        for (const [key, value] of Object.entries<string>(rest.params as RouteParams<T>)) {
          newPath = newPath.replace(`:${key}`, value);
        }
      }
      const params = 'params' in rest ? (rest.params as RouteParams<T>) : {};

      const shouldBlockFn = nonReactiveInterRouter.getState().shouldBlockFn;
      if (shouldBlockFn) {
        const next = {
          fullPath: to,
          params,
          pathname: newPath,
          search,
          isHandledBySettingsRouter: true,
        };
        if (shouldBlockFn({ current: getCurrentLocationContext(), next })) return;
      }

      if (settingsRouterPath.path !== newPath) {
        setSettingsRouterPathHistory({ path: newPath, raw: to, label: constructLabel(to, params), search, replace });
        setSettingsRouterPath({ path: newPath, raw: to, search, context, replace });
      }

      if (!isSettingsOpen) {
        setIsSettingsOpen(true);
        setOriginPageLabel(originLabel);
      }
    },
    [settingsRouterPath.path, isSettingsOpen]
  );

  // Go back to the previous settings route. Called when clicking the navigation button in the bottom left of the
  // settings tray.
  const goBack = useCallback(() => {
    const { lastItem } = getBackSettings() ?? {};
    const shouldBlockFn = nonReactiveInterRouter.getState().shouldBlockFn;

    if (lastItem) {
      if (shouldBlockFn) {
        const next = {
          fullPath: lastItem?.raw ?? '',
          params: {},
          pathname: lastItem?.path ?? '',
          search: lastItem?.search ?? {},
          isHandledBySettingsRouter: true,
        };

        if (shouldBlockFn({ current: getCurrentLocationContext(), next })) return;
      }

      goBackSettings();
      setSettingsRouterPath({ path: lastItem.path, raw: lastItem.raw, search: lastItem.search });
      history.back();
    } else {
      goBackSettings();
      closeSettings();
    }
  }, []);

  const navigateMain = useCallback(({ to, ...rest }: MainNavigateArgs) => {
    setMainRouterPath({ path: to, ...rest });
  }, []);

  const getCurrentLocationContext = () => ({
    fullPath: '',
    params: {},
    pathname: history.location.hash,
    search: history.location.search,
    isHandledBySettingsRouter: true,
  });

  return {
    navigate,
    goBack,
    closeSettings,
    context: context as C | undefined,
    isOpen: isSettingsOpen,
    navigateMain,
  };
};
